/**
 * Card Match browser-source entrypoint.
 *
 * URL params:
 *   key       — API key (required for non-mock mode)
 *   ws        — WS port (default 3001) for local dev
 *   mock=1    — skip WS and run a scripted demo for visual review
 *   viewer=1  — viewer mode (read-only watch feed)
 *   handle=X  — handle when in viewer mode
 *
 * Audio is gated to streamer mode only — viewers receive a silent no-op
 * facade so the AudioContext never instantiates inside the iframe.
 */

import type { LbRow, OutMsg } from '@sg/shared';
import { readViewerMode, wsUrlFor } from '@sg/shared';
import { WsClient } from './ws/client.js';
import { resolveWsBase } from './ws/ws-base.js';
import { Store } from './state/store.js';
import type { SceneName } from './state/types.js';
import { createDomStage } from './scene/lib/stage.js';
import { applyTheme, DEFAULT_THEME } from './scene/lib/theme.js';
import { createLobby } from './scene/lobby.js';
import { createRound } from './scene/round.js';
import { createReveal } from './scene/reveal.js';
import { createPodium } from './scene/podium.js';
import { createConnectionLost } from './scene/connection-lost.js';
import { createRoundSplash } from './ui/round-splash.js';
import { createSideLeaderboard } from './ui/side-leaderboard.js';
import { createAnimatedBg } from './ui/animated-bg.js';
import { mountAudioUnlock } from './ui/audio-unlock.js';
import { createConflictBanner } from './ui/conflict-banner.js';
import { audioBus } from './audio/bus.js';
import { sfx as sfxImpl } from './audio/sfx.js';
import { music as musicImpl } from './audio/music.js';
import { mountWatchQrWidget } from '../../../shared/qrcode-generator/watch-qr.js';

/** Slug of THIS browser source. */
const MY_SLUG = 'card-match';

/** How long to hold the round scene with every tile face-up before
 *  transitioning to the reveal scene. Backend's REVEAL_HOLD_MS is 6s; we
 *  use ~2.4s here so the reveal scene + podium routing still feel snappy
 *  while giving chat a clear view of the unmatched tiles on timeout. */
const REVEAL_HOLD_MS = 2400;

applyTheme(DEFAULT_THEME);

const params = new URL(window.location.href).searchParams;
const mockMode = params.get('mock') === '1';
const viewerMode = readViewerMode(params);
const isViewer = viewerMode.kind === 'viewer';
const key = viewerMode.kind === 'streamer' ? viewerMode.key : 'dev-no-key';
const wsPort = Number(params.get('ws') ?? '3001');

// Viewer-aware audio facades — silent no-ops in viewer mode so the
// AudioContext never instantiates. Mirrors the Anagram pattern.
const sfx: typeof sfxImpl = isViewer
  ? ({
      preload: async () => {},
      play: () => {},
      cardFlip: () => {},
      match: () => {},
      mismatch: () => {},
      roundStart: () => {},
      roundWin: () => {},
      timeout: () => {},
      tickWarning: () => {},
      scoreChime: () => {},
    } as unknown as typeof sfxImpl)
  : sfxImpl;
const music: typeof musicImpl = isViewer
  ? ({
      play: () => {},
      stop: () => {},
      resumeIfPending: () => {},
    } as unknown as typeof musicImpl)
  : musicImpl;

const root = document.getElementById('root')!;
root.innerHTML = '';
root.style.cssText = 'position: relative; width: 100vw; height: 100vh; overflow: hidden;';
root.setAttribute('data-testid', 'browser-root');

const stage = createDomStage({ mount: root });
const stageEl = stage.el;

// Animated background sits behind every scene.
createAnimatedBg(stageEl);

const lobby = createLobby(stageEl);
const round = createRound(stageEl);
const reveal = createReveal(stageEl);
const podium = createPodium(stageEl);
const connectionLost = createConnectionLost(stageEl);
const sideLeaderboard = createSideLeaderboard(stageEl);
const splash = createRoundSplash(stageEl);
// Cross-game conflict banner — backend fans `cross_game_conflict` to every
// connected browser source for the tenant; we filter by MY_SLUG below.
const conflictBanner = createConflictBanner(stageEl);

if (!isViewer) {
  mountAudioUnlock();
}

sideLeaderboard.setVisible(false);

if (!isViewer) {
  // Pre-fetch SFX buffers; lobby bed starts immediately so OBS
  // page-permissions kick it off without a gesture.
  void sfx.preload();
  music.play('lobby');
}

const allScenes: Record<SceneName, { setVisible(v: boolean): void }> = {
  lobby,
  round,
  reveal,
  podium,
  connection_lost: connectionLost,
};

function applyScene(next: SceneName): void {
  for (const [name, h] of Object.entries(allScenes)) {
    h.setVisible(name === next);
  }
  root.setAttribute('data-scene', next);
  (window as unknown as { __cm_scene?: SceneName }).__cm_scene = next;
  sideLeaderboard.setVisible(next === 'round' || next === 'reveal');
  // Music routing per scene.
  if (next === 'lobby') {
    music.play('lobby');
  } else if (next === 'round') {
    music.play('round');
  } else if (next === 'reveal' || next === 'podium') {
    // Stop the round bed; reveal/podium ride on SFX (round-win / fanfare).
    music.stop();
  }
}

const store = new Store();
applyScene(store.getState().scene);

let lastRoundIndex = -1;
// Round-end timestamp drives the final-10s tick warning.
let roundEndsAt = 0;
let lastTickSecond = -1;
let watchQrCtrl: ReturnType<typeof mountWatchQrWidget> | null = null;
let showWatchQr = true;
let currentHandle: string | null = null;

function applyWatchQrVisibility(handle: string | null): void {
  if (isViewer) return;
  if (!showWatchQr) {
    if (watchQrCtrl) { watchQrCtrl.destroy(); watchQrCtrl = null; }
    return;
  }
  if (watchQrCtrl) {
    watchQrCtrl.update(handle);
  } else {
    watchQrCtrl = mountWatchQrWidget({ handle });
  }
}

async function handleWs(msg: OutMsg): Promise<void> {
  store.dispatchWs(msg);
  switch (msg.type) {
    case 'cm_round_open': {
      const idx = msg.round_index;
      if (idx !== lastRoundIndex) {
        lastRoundIndex = idx;
        splash.show(`ROUND ${idx + 1}`, `${msg.grid_w} × ${msg.grid_h}`);
      }
      sfx.roundStart();
      // music.play('round') is also fired by applyScene when scene flips;
      // re-asserting here is harmless and keeps the bed swap deterministic.
      music.play('round');
      roundEndsAt = msg.ends_at_ms;
      lastTickSecond = -1;
      break;
    }
    case 'cm_tile_flip': {
      // Card-flip whoosh on every chat-driven flip pair (match or miss).
      sfx.cardFlip();
      // Schedule a clear-flip dispatch after the peek window so non-matching
      // tiles flip back face-down. Permanent reveals come via
      // cm_tile_reveal_permanent which beats this clear out.
      const peekMs = Math.max(0, msg.peek_ends_at_ms - Date.now());
      const positions: [number, number] = [msg.positions[0], msg.positions[1]];
      setTimeout(() => {
        store.dispatch({ type: 'clear-flip', positions });
      }, peekMs + 50);
      // Match/mismatch cues fire after a short delay so they layer on top of
      // the flip whoosh — feels chunkier than firing simultaneously.
      if (msg.match) {
        setTimeout(() => {
          sfx.match();
          sfx.scoreChime();
        }, 220);
      } else {
        setTimeout(() => sfx.mismatch(), 220);
      }
      break;
    }
    case 'cm_round_end': {
      roundEndsAt = 0;
      if (msg.reason === 'all_matched') {
        sfx.roundWin();
        music.play('victory');
      } else {
        sfx.timeout();
        // Drop to lobby bed so the timeout sting plays cleanly. The reveal
        // scene handoff is delayed below so chat can see the full board.
        setTimeout(() => music.play('lobby'), 600);
      }
      // Hold the round scene with every tile flipped face-up so chat sees
      // what they missed (or completed) before the reveal scene takes over.
      // Reducer already populated the tiles + pendingReveal payload via
      // dispatchWs above — we just have to schedule the transition.
      setTimeout(() => {
        store.dispatch({ type: 'cm-finalize-reveal' });
      }, REVEAL_HOLD_MS);
      break;
    }
    case 'cm_session_leaderboard':
      sideLeaderboard.setSessionRows(
        msg.rows.map((r) => ({
          rank: r.rank,
          player_id: r.player_id,
          display_name: r.display_name,
          total_points: r.total_points,
        })),
      );
      break;
    case 'cross_game_conflict':
      // Banner only matters for the slug being attempted OR the slug currently
      // running — other game scenes shouldn't surface the warning.
      if (msg.attempted_slug === MY_SLUG || msg.running_slug === MY_SLUG) {
        conflictBanner.show(msg.message);
      }
      break;
    case 'state_snapshot': {
      const snapSettings = (msg as any).state?.settings;
      const nextShow = snapSettings && typeof snapSettings.show_watch_qr === 'boolean' ? snapSettings.show_watch_qr : true;
      if (nextShow !== showWatchQr) {
        showWatchQr = nextShow;
        applyWatchQrVisibility(currentHandle);
      }
      break;
    }
    case 'auth_ok' as OutMsg['type']: {
      const authMsg = msg as unknown as { type: 'auth_ok'; handle: string | null };
      currentHandle = authMsg.handle;
      applyWatchQrVisibility(authMsg.handle);
      break;
    }
    default:
      break;
  }
}

store.subscribe((next, prev) => {
  if (next.scene !== prev.scene) applyScene(next.scene);
  if (next.theme && next.theme !== prev.theme) applyTheme(next.theme);
  if (next.scene === 'lobby') {
    lobby.setLeaderboard(next.leaderboard, next.leaderboardAllTime);
  }
  if (next.scene === 'round' && next.round) {
    round.setRound(next.round);
  }
  if (next.scene === 'reveal' && next.reveal) {
    reveal.setReveal(next.reveal);
  }
  if (next.scene === 'podium' && next.podium) {
    podium.setPodium(next.podium);
  }
  if (next.leaderboardAllTime !== prev.leaderboardAllTime) {
    sideLeaderboard.setAllTimeRows(next.leaderboardAllTime);
  }
  if (next.sessionLeaderboard !== prev.sessionLeaderboard) {
    sideLeaderboard.setSessionRows(next.sessionLeaderboard);
  }
});

setInterval(() => {
  if (store.getState().scene === 'round') round.tick();
}, 200);

// Tick-warning driver: once per second in the final 10s of the round.
setInterval(() => {
  if (roundEndsAt <= 0) return;
  const remainingMs = roundEndsAt - Date.now();
  if (remainingMs <= 0 || remainingMs > 10_000) return;
  const secondsLeft = Math.ceil(remainingMs / 1000);
  if (secondsLeft !== lastTickSecond && secondsLeft >= 1 && secondsLeft <= 10) {
    lastTickSecond = secondsLeft;
    sfx.tickWarning();
  }
}, 200);

let wsClient: WsClient | null = null;
function connectWs(): void {
  const wsUrl = wsUrlFor(viewerMode, resolveWsBase(wsPort), { game: MY_SLUG });
  wsClient = new WsClient({
    url: wsUrl,
    sessionToken: key,
    viewer: isViewer,
    onMessage: (m) => {
      void handleWs(m);
    },
    onState: (s) => {
      store.dispatch({ type: 'connection', value: s });
      if (!isViewer) connectionLost.setVisible(s !== 'open');
    },
  });
  wsClient.connect();
  (window as unknown as Record<string, unknown>).__cm_ws = wsClient;
}

if (mockMode) {
  // Minimal mock so a developer can pull up the lobby visually.
  const fake: LbRow[] = [
    {
      rank: 1,
      player_id: 1,
      display_name: 'AlphaWolf',
      avatar_seed: 'alphawolf',
      total_points: 1240,
      games_played: 10,
      games_won: 4,
    } as LbRow,
  ];
  lobby.setLeaderboard(fake, fake);
  if (!isViewer) void audioBus.unlock();
} else {
  connectWs();
}

if (!mockMode) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (wsClient) {
        try { wsClient.close(); } catch { /* swallow */ }
        wsClient = null;
      }
    } else if (document.visibilityState === 'visible') {
      if (!wsClient) connectWs();
    }
  });
}

if (!isViewer) {
  // Belt-and-braces: any pointerdown unlocks audio (some OBS configs need it).
  window.addEventListener(
    'pointerdown',
    () => {
      void audioBus.unlock();
    },
    { once: true },
  );
}

(window as unknown as Record<string, unknown>).__cm_store = store;
(window as unknown as Record<string, unknown>).__cm_handleWs = handleWs;
